import { ApiError, formatUsPhone, queryKeys, type Vendor } from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
} from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { ArrowLeft, Truck } from 'lucide-react'
import { useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { LinkedTasksCard } from '../components/linked-tasks-card.js'
import { MarkImportantButton } from '../components/mark-important.js'
import { PaymentsCard } from '../components/payments-card.js'
import { VendorCreditCard } from '../components/vendor-returns.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { EditVendorDialog } from './vendors.js'

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-2xs font-medium uppercase tracking-wide text-foreground-muted">
        {label}
      </dt>
      <dd className="mt-0.5 whitespace-pre-line text-sm">{children}</dd>
    </div>
  )
}

export function VendorDetailPage() {
  const { vendorId } = useParams({ from: '/authed/vendors/$vendorId' })
  const { can } = useAuth()
  const [editing, setEditing] = useState(false)

  const {
    data: vendor,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.vendors.detail(vendorId),
    queryFn: () => api.get<Vendor>(`/vendors/${vendorId}`),
    enabled: can('inventory:read'),
  })

  const back = (
    <div className="pb-3">
      <Link
        to="/vendors"
        className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" aria-hidden="true" /> All vendors
      </Link>
    </div>
  )

  if (isLoading) {
    return (
      <Page>
        {back}
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-64 w-full" />
      </Page>
    )
  }

  if (!vendor) {
    return (
      <Page>
        {back}
        <EmptyState
          icon={Truck}
          title={
            error instanceof ApiError && error.status === 404
              ? 'Vendor not found'
              : 'Could not load this vendor'
          }
          description="It may have been removed, or you may not have access to vendors."
        />
      </Page>
    )
  }

  return (
    <Page>
      {back}
      <PageHeader
        title={vendor.name}
        description={vendor.address ?? undefined}
        actions={
          <>
            <MarkImportantButton itemType="vendor" itemId={vendorId} />
            {!vendor.isActive ? <Badge tone="danger">Inactive</Badge> : null}
            {can('inventory:write') ? (
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                Edit
              </Button>
            ) : null}
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <VendorCreditCard vendorId={vendor.id} canWrite={can('inventory:write')} />
          <PaymentsCard vendorId={vendor.id} />
          <LinkedTasksCard vendorId={vendor.id} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3">
              <DetailRow label="Online order link">
                {vendor.orderUrl ? (
                  <a
                    href={vendor.orderUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-primary hover:underline"
                  >
                    {vendor.orderUrl}
                  </a>
                ) : (
                  '—'
                )}
              </DetailRow>
              {vendor.contacts.length === 0 ? <DetailRow label="Contacts">—</DetailRow> : null}
              {vendor.contacts.map((contact, i) => (
                <DetailRow
                  // biome-ignore lint/suspicious/noArrayIndexKey: contacts have no id
                  key={i}
                  label={
                    [contact.name, contact.role].filter(Boolean).join(' · ') || `Contact ${i + 1}`
                  }
                >
                  {contact.phones.map((p) => (
                    <div key={`p-${p.number}`}>
                      <a href={`tel:${p.number}`} className="text-primary hover:underline">
                        {formatUsPhone(p.number)}
                      </a>{' '}
                      <span className="text-foreground-muted">({p.type})</span>
                    </div>
                  ))}
                  {contact.emails.map((e) => (
                    <div key={`e-${e.address}`}>
                      <a href={`mailto:${e.address}`} className="text-primary hover:underline">
                        {e.address}
                      </a>{' '}
                      <span className="text-foreground-muted">({e.type})</span>
                    </div>
                  ))}
                </DetailRow>
              ))}
              <DetailRow label="Address">{vendor.address ?? '—'}</DetailRow>
              <DetailRow label="Notes">{vendor.notes ?? '—'}</DetailRow>
            </dl>
          </CardContent>
        </Card>
      </div>

      <EditVendorDialog vendor={editing ? vendor : null} onClose={() => setEditing(false)} />
    </Page>
  )
}
